import React, { useState } from 'react';
import { Link, useLocation } from 'react-router-dom';
import { X, Menu, Home as HomeIcon, Building2, Users, CheckSquare, Calendar, Mail, BarChart3, Upload, Settings, Shield } from 'lucide-react';
import { Button } from '@/components/ui/button';

const MobileNav = ({ user, onLogout }) => {
  const [isOpen, setIsOpen] = useState(false);
  const location = useLocation();

  const toggleMenu = () => setIsOpen(!isOpen);
  const closeMenu = () => setIsOpen(false);

  const navItems = user?.is_super_admin ? [
    { to: '/saas-admin', icon: Shield, label: 'SaaS Admin', testId: 'nav-saas-admin' },
    { to: '/saas-admin/tenants', icon: Building2, label: 'Tenants', testId: 'nav-saas-tenants' },
    { to: '/saas-admin/users', icon: Users, label: 'Users', testId: 'nav-saas-users' },
  ] : [
    { to: '/', icon: HomeIcon, label: 'Leads', testId: 'nav-leads' },
    { to: '/companies', icon: Building2, label: 'Companies', testId: 'nav-companies' },
    { to: '/contacts', icon: Users, label: 'Contacts', testId: 'nav-contacts' },
    { to: '/tasks', icon: CheckSquare, label: 'Tasks', testId: 'nav-tasks' },
    { to: '/calendar', icon: Calendar, label: 'Calendar', testId: 'nav-calendar' },
    { to: '/email-sync', icon: Mail, label: 'Email Sync', testId: 'nav-email-sync' },
    { to: '/analytics', icon: BarChart3, label: 'Analytics', testId: 'nav-analytics' },
    { to: '/import', icon: Upload, label: 'Import', testId: 'nav-import' },
    { to: '/admin', icon: Settings, label: 'Admin Panel', testId: 'nav-admin', adminOnly: true },
  ];

  const filteredNavItems = navItems.filter(item => 
    !item.adminOnly || user?.role === 'admin'
  );

  return (
    <>
      {/* Mobile Menu Button */}
      <Button
        variant="ghost"
        size="sm"
        onClick={toggleMenu}
        className="md:hidden"
        aria-label="Toggle menu"
      >
        <Menu className="w-5 h-5" />
      </Button>

      {/* Mobile Menu Overlay */}
      {isOpen && (
        <div
          className="fixed inset-0 bg-black/50 z-40 md:hidden"
          onClick={closeMenu}
        />
      )}

      {/* Mobile Menu Sidebar */}
      <div
        className={`fixed top-0 left-0 h-full w-64 bg-white shadow-xl z-50 transform transition-transform duration-300 ease-in-out md:hidden ${
          isOpen ? 'translate-x-0' : '-translate-x-full'
        }`}
      >
        <div className="flex flex-col h-full">
          {/* Header */}
          <div className="flex items-center justify-between p-4 border-b">
            <h2 className="text-lg font-bold text-slate-900">Menu</h2>
            <Button
              variant="ghost"
              size="sm"
              onClick={closeMenu}
              aria-label="Close menu"
            >
              <X className="w-5 h-5" />
            </Button>
          </div>

          {/* Navigation Links */}
          <nav className="flex-1 overflow-y-auto p-4">
            <ul className="space-y-2">
              {filteredNavItems.map((item) => {
                const Icon = item.icon;
                const isActive = item.to === '/' 
                  ? location.pathname === '/'
                  : location.pathname.startsWith(item.to);
                
                return (
                  <li key={item.to}>
                    <Link
                      to={item.to}
                      data-testid={item.testId}
                      onClick={closeMenu}
                      className={`flex items-center gap-3 px-4 py-3 rounded-lg transition-colors ${
                        isActive
                          ? 'bg-indigo-100 text-indigo-700 font-medium'
                          : 'text-slate-700 hover:bg-slate-100'
                      }`}
                    >
                      <Icon className="w-5 h-5 flex-shrink-0" />
                      <span>{item.label}</span>
                    </Link>
                  </li>
                );
              })}
            </ul>
          </nav>

          {/* Footer with User Info */}
          {user && (
            <div className="border-t p-4">
              <div className="mb-3">
                <p className="text-sm font-medium text-slate-900">
                  {user.first_name} {user.last_name}
                </p>
                <p className="text-xs text-slate-500">{user.email}</p>
              </div>
              <Button
                variant="outline"
                size="sm"
                onClick={() => {
                  closeMenu();
                  onLogout();
                }}
                className="w-full"
              >
                Logout
              </Button>
            </div>
          )}
        </div>
      </div>
    </>
  );
};

export default MobileNav;
